import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, FileText } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import {
  type OrderLineItemInput,
  type Proceeding,
  useGenerateOrder,
  useProjectOptions,
} from "@/features/rfq/api/proceedings";

/**
 * Wygenerowanie Zamówienia ze zwycięskiej oferty — modal z pełnym prefillem.
 *
 * Oferta RFQ to jedna kwota netto, a Zamówienie liczy wartość z pozycji, więc domyślnie
 * zakładamy JEDNĄ pozycję (tytuł postępowania na kwotę oferty). Kupiec może ją rozbić.
 * Wykonawcę i Zamawiającego ustala serwer z postępowania — tu ich nie ruszamy.
 */
export function GenerateOrderModal({
  proceeding,
  open,
  onClose,
}: {
  proceeding: Proceeding;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: projects } = useProjectOptions();
  const generate = useGenerateOrder(proceeding.id);

  const [project, setProject] = useState(proceeding.project ?? "");
  const [deadline, setDeadline] = useState(proceeding.delivery_deadline ?? "");
  const [description, setDescription] = useState(proceeding.description ?? "");
  const [items, setItems] = useState<OrderLineItemInput[]>([
    {
      // Pełny numer postępowania w nazwie wiąże pozycję z konkretnym przetargiem.
      name: `${proceeding.title} (${proceeding.number})`,
      unit: "usługa",
      quantity: "1",
      unit_rate: proceeding.winning_amount_net ?? "0",
    },
  ]);
  const [error, setError] = useState("");

  const total = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_rate) || 0),
        0,
      ),
    [items],
  );

  const setItem = (i: number, patch: Partial<OrderLineItemInput>) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () =>
    setItems((prev) => [...prev, { name: "", unit: "usługa", quantity: "1", unit_rate: "0" }]);
  const removeItem = (i: number) =>
    setItems((prev) => prev.filter((_, idx) => idx !== i));

  const submit = () => {
    if (!project) return setError("Wskaż Przedsięwzięcie — numer zamówienia bierze z niego skrót.");
    if (!deadline) return setError("Termin realizacji jest wymagany.");
    const cleaned = items.filter((it) => it.name.trim());
    if (cleaned.length === 0) return setError("Dodaj co najmniej jedną pozycję realizacyjną.");
    setError("");
    generate.mutate(
      { project, deadline, description, line_items: cleaned },
      {
        onSuccess: (order) => {
          onClose();
          navigate(`/orders/${order.id}`);
        },
        onError: (e: unknown) => {
          const detail =
            (e as { response?: { data?: { errors?: { detail?: string } } } })?.response
              ?.data?.errors?.detail;
          setError(detail || "Nie udało się wygenerować zamówienia.");
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Generuj zamówienie" size="lg">
      <div className="space-y-4">
        <div className="flex items-start gap-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-3 text-sm">
          <FileText size={16} className="mt-0.5 shrink-0 text-[var(--color-text-muted)]" />
          <p className="text-[var(--color-text-secondary)]">
            Zamówienie trafi do zwycięzcy:{" "}
            <strong className="text-[var(--color-text-primary)]">
              {proceeding.winning_contractor_name}
            </strong>
            . Pozycje i termin są wstępnie wypełnione z postępowania — możesz je zmienić
            przed utworzeniem.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
              Przedsięwzięcie *
            </span>
            <SearchableSelect
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder="— wybierz —"
              options={[
                { value: "", label: "— wybierz —" },
                ...(projects ?? []).map((p) => ({ value: p.id, label: p.name })),
              ]}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
              Termin realizacji *
            </span>
            <DatePicker value={deadline} onChange={setDeadline} />
          </label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">
              Pozycje realizacyjne
            </span>
            <Button variant="secondary" size="sm" onClick={addItem}>
              <Plus size={13} className="mr-1 inline" /> Dodaj pozycję
            </Button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <label className="min-w-[12rem] flex-1">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    Nazwa
                  </span>
                  <input
                    value={it.name}
                    onChange={(e) => setItem(i, { name: e.target.value })}
                    className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
                  />
                </label>
                <label className="w-20">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    Jedn.
                  </span>
                  <input
                    value={it.unit}
                    onChange={(e) => setItem(i, { unit: e.target.value })}
                    className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm text-[var(--color-text-primary)]"
                  />
                </label>
                <label className="w-20">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    Ilość
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.quantity}
                    onChange={(e) => setItem(i, { quantity: e.target.value })}
                    className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-right text-sm text-[var(--color-text-primary)]"
                  />
                </label>
                <label className="w-32">
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    Cena netto
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={it.unit_rate}
                    onChange={(e) => setItem(i, { unit_rate: e.target.value })}
                    className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-right text-sm text-[var(--color-text-primary)]"
                  />
                </label>
                <button
                  onClick={() => removeItem(i)}
                  disabled={items.length === 1}
                  className="mb-1.5 p-1.5 text-[var(--color-text-muted)] hover:text-red-600 disabled:opacity-30"
                  title="Usuń pozycję"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-right text-sm text-[var(--color-text-secondary)]">
            Wartość netto:{" "}
            <strong className="text-[var(--color-text-primary)]">
              {formatAmount(total)} zł
            </strong>
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">
            Opis zamówienia
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
          />
        </label>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-3">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} disabled={generate.isPending}>
            <FileText size={14} className="mr-1.5 inline" /> Utwórz zamówienie
          </Button>
        </div>
      </div>
    </Modal>
  );
}
